/**
 * Client HTTP typé de l'API Silex — console tuteur / admin (web).
 *
 * Adapté de `apps/mobile/src/api/client.ts` (SLX-044) :
 * - Base URL : `import.meta.env.VITE_API_URL` (injectée au BUILD par Vite —
 *   cf. `vite.config.ts`), repli `http://localhost:3000`.
 * - `Authorization: Bearer <accessToken>` sur les appels `auth: true`.
 * - Sur `401`, tente **un seul** refresh via `POST /v1/auth/refresh`, rejoue la
 *   requête, puis abandonne (`onAuthLost`) si le refresh échoue aussi.
 * - Toutes les formes de données viennent du contrat `@silex/shared`.
 *
 * L'access token vit en mémoire, le refresh token dans `localStorage` — voir
 * `./tokens.ts`. Le contexte React (`../auth/AuthContext`) branche `onAuthLost`
 * pour fermer la session côté UI.
 */
import type {
  BlocParcoursBody,
  CreerParcoursBody,
  EtatAntiRobotsResponse,
  EtatParcours,
  LeveeBlocageResponse,
  Matiere,
  NiveauScolaire,
  ParcoursEleveDto,
  AccessTokenClaims,
  AccountRole,
  AffectationResponse,
  ApiErrorBody,
  AidesDevoirsEleveResponse,
  AuthenticatedUser,
  ChangerMotDePasseRequest,
  ChangerMotDePasseResponse,
  CapsuleRelectureResponse,
  ChangerStatutSavoirBody,
  CopieDto,
  CopiesEleveResponse,
  ChapitreProgrammeResponse,
  ChapitresProgrammeResponse,
  ExerciceRelectureResponse,
  RelectureExerciceBody,
  RelectureExerciceResponse,
  RetraitExerciceResponse,
  ChildProfile,
  CompteAdminDto,
  CorpusEditionRequest,
  CorpusFiltres,
  CorpusItemDetail,
  CorpusListeResponse,
  CreerAffectationRequest,
  CreerCompteAdminRequest,
  CreerEnfantAdminRequest,
  DeposerSequenceBody,
  DoseEleveResponse,
  ElevesSuivisResponse,
  ElevesDuTuteurResponse,
  FicheRelectureResponse,
  EvaluationsAValiderResponse,
  EvaluationsEleveResponse,
  EvolutionEleveResponse,
  ExercicesChapitreResponse,
  FileTuteurResponse,
  GroupeUsageIa,
  ListeAffectationsResponse,
  ListeComptesResponse,
  ListeConnexionsResponse,
  ListeElevesAdminResponse,
  OctroiQuotaResponse,
  LoginRequest,
  ModifierObjectifBody,
  ModifierSequenceBody,
  NiveauMaximalResponse,
  NoterSeanceBody,
  ObjectifResponse,
  LoginResponse,
  MfaConfirmationResponse,
  MfaEnrolementResponse,
  ObservationsSavoirResponse,
  OuvrirSeanceBody,
  PeriodeDose,
  PlanActifResponse,
  PoserObjectifBody,
  PreparationSeanceResponse,
  ProgressionEleveResponse,
  RapportEvaluationDto,
  RapportUniqueResponse,
  RecommandationsEleveResponse,
  RefreshResponse,
  ReglerNiveauMaximalBody,
  RelectureSavoirResponse,
  ReinitialisationMfaResponse,
  ReporterUniteRequest,
  ReporterUniteResponse,
  ReprendreUniteRequest,
  RevueEvaluationRequest,
  RevueEvaluationResponse,
  SavoirChapitreResponse,
  SeanceTuteurResponse,
  SeancesTuteurResponse,
  SequenceTuteurResponse,
  SequencesTuteurResponse,
  SorteSavoir,
  StatutSavoirResponse,
  PagesDeTravailTuteurResponse,
  EcrireNoteDuTuteurBody,
  NoteDuTuteurResponse,
  NotesDuTravailResponse,
  TravauxTuteurResponse,
  UsageIaResponse,
  IndicateursRetroactionResponse,
  UserRole,
  VueEleveTuteurResponse,
  EtatCapacitesResponse,
  ReglerCapacitesResponse,
} from '@silex/shared';
import {
  CHAMP_DEFI_ANTI_ROBOT,
  DEFI_ANTI_ROBOT_REFUSE_CODE,
  MOT_DE_PASSE_ACTUEL_INCORRECT_CODE,
  lireCapacites,
} from '@silex/shared';

import { prendreJeton, signalerRefus } from '../auth/DefiAntiRobot';
import { enTetesVoirComme } from '../lib/voir-comme';

import { tokenStore } from './tokens';

const BASE_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * Les filtres de « Travaux et corrections » (MV-TUT-07), tels que l'adresse de
 * l'onglet les porte : des chaînes, que le serveur valide. Vide ou absent :
 * pas de filtre.
 */
export interface FiltresTravaux {
  readonly genre?: string;
  readonly notion?: string;
  readonly verdict?: string;
  readonly erreur?: string;
}

/** Erreur normalisée : porte le code stable et le statut HTTP de l'API. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, body: ApiErrorBody | undefined, fallback: string) {
    super(body?.error?.message ?? fallback);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.error?.code ?? 'UNKNOWN';
    this.details = body?.error?.details;
  }
}

/** L'API répond `401 { code: 'MFA_REQUIRED' }` quand l'OTP manque ou est faux. */
export const MFA_REQUIRED_CODE = 'MFA_REQUIRED';

/**
 * AB#296 — `403 { code: 'MFA_ENROLEMENT_REQUIS' }` : le rôle impose le double
 * facteur et le compte ne l'a pas enrôlé. La session ne sert qu'à l'enrôlement.
 */
export const MFA_ENROLEMENT_REQUIS_CODE = 'MFA_ENROLEMENT_REQUIS';

let onAuthLostHandler: () => void = () => {};

/** Branché par `AuthProvider` : appelé quand la session ne peut plus être tenue. */
export function setOnAuthLost(handler: () => void): void {
  onAuthLostHandler = handler;
}

interface RequestOptions {
  // LOT 3.4 — `DELETE` s'ajoute pour le RETRAIT d'une affectation. Il ne
  // supprime rien côté serveur : la route date `fin` et conserve la ligne
  // (§10.8, l'historique de qui a suivi un mineur). Le verbe est celui du
  // contrat, pas une description de l'effet.
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Ajoute l'en-tête Authorization + gère le rejeu après refresh. */
  auth?: boolean;
  /** La réponse réussie est un DOCUMENT (HTML), pas du JSON — rétroaction P1-E. */
  texte?: boolean;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

async function rawRequest(path: string, opts: RequestOptions): Promise<Response> {
  /* « Voir comme » (F-84) : le choix de la session part avec chaque requête ; le
     serveur décide s'il l'honore. */
  const headers: Record<string, string> = { Accept: 'application/json', ...enTetesVoirComme() };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.auth) {
    const token = tokenStore.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return fetch(`${BASE_URL}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
}

// Un seul refresh à la fois : les requêtes concurrentes partagent la promesse.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshOnce(): Promise<boolean> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return false;
  try {
    const res = await rawRequest('/v1/auth/refresh', {
      method: 'POST',
      body: { refreshToken },
    });
    if (!res.ok) return false;
    const tokens = (await parseBody(res)) as RefreshResponse | undefined;
    if (!tokens?.accessToken || !tokens?.refreshToken) return false;
    tokenStore.setAccessToken(tokens.accessToken);
    tokenStore.setRefreshToken(tokens.refreshToken);
    /* MV-SOC-01 — le serveur relit les capacités à chaque renouvellement. Ce
       qu'il rend REMPLACE ce que la session portait ; sans le champ, éteint. */
    tokenStore.setCapacites(lireCapacites(tokens.capacites));
    return true;
  } catch {
    return false;
  }
}

/**
 * LE renouvellement en cours, partagé par TOUS ses appelants — y compris la
 * reprise de session au démarrage (`api.refresh`).
 *
 * Revue UX `UX-7-39` (Φ-MV-7) : un jeton de rafraîchissement ne sert qu'une
 * fois. Deux reprises lancées dans le même instant (l'effet de démarrage rejoué
 * par `StrictMode`, deux rendus rapprochés) envoyaient deux fois le MÊME jeton ;
 * la seconde était refusée, et son refus vidait la session que la première
 * venait de reprendre — la console renvoyait à `/login`. Constaté en local le
 * 8 octobre 2026 : deux passages sur trois ouvraient la page de connexion.
 */
function renouvelerUneFois(): Promise<boolean> {
  refreshInFlight =
    refreshInFlight ??
    tryRefreshOnce().finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await rawRequest(path, opts);

  if (res.status === 401 && opts.auth) {
    const corps401 = (await parseBody(res)) as ApiErrorBody | undefined;
    // AB#747 — « mot de passe actuel incorrect » est un refus de SAISIE, pas
    // de session : on ne renouvelle rien, on le rend tel quel.
    if (corps401?.error?.code === MOT_DE_PASSE_ACTUEL_INCORRECT_CODE) {
      throw new ApiError(401, corps401, 'Mot de passe actuel incorrect');
    }
    const refreshed = await renouvelerUneFois();
    if (!refreshed) {
      onAuthLostHandler();
      throw new ApiError(401, corps401, 'Session expirée');
    }
    res = await rawRequest(path, opts);
  }

  if (opts.texte && res.ok) return (await res.text()) as T;
  const body = await parseBody(res);
  if (!res.ok) {
    const erreur = new ApiError(
      res.status,
      body as ApiErrorBody | undefined,
      `Erreur ${res.status}`,
    );
    // AB#884 — le défi refusé : le widget le dit et se refait.
    if (res.status === 403 && erreur.code === DEFI_ANTI_ROBOT_REFUSE_CODE) signalerRefus();
    throw erreur;
  }
  return body as T;
}

/**
 * Décode (sans vérifier la signature) les revendications d'un access token JWT.
 *
 * L'API n'expose pas encore de `GET /v1/auth/me`. En attendant, on lit le rôle
 * directement dans le jeton pour piloter l'affichage. **Ce n'est pas une
 * frontière de confiance** : le RBAC réel est appliqué côté API à chaque appel.
 */
function decodeClaims(token: string): AccessTokenClaims | null {
  const segments = token.split('.');
  const payloadB64 = segments[1];
  if (segments.length !== 3 || !payloadB64) return null;
  try {
    const normalised = payloadB64.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised.padEnd(normalised.length + ((4 - (normalised.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      window
        .atob(padded)
        .split('')
        .map((ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const raw = JSON.parse(json) as Partial<AccessTokenClaims>;
    if (
      typeof raw.sub === 'string' &&
      typeof raw.role === 'string' &&
      typeof raw.tenantId === 'string'
    ) {
      return {
        sub: raw.sub,
        role: raw.role as UserRole,
        tenantId: raw.tenantId,
        ...(typeof raw.eleveId === 'string' ? { eleveId: raw.eleveId } : {}),
        ...(raw.mfaEnrolementRequis === true ? { mfaEnrolementRequis: true as const } : {}),
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** `?a=b` si la query n'est pas vide, chaîne vide sinon. */
function suffixe(qs: URLSearchParams): string {
  const s = qs.toString();
  return s ? `?${s}` : '';
}

/* ---------------------------------------------------------------------- */
/* Le parcours, côté tuteur (P3, AB#975) — `CONTRAT-PARCOURS.md`.          */
/* Les formes qui ne sont pas encore dans `@silex/shared` vivent ici, et   */
/* se règlent au contrat d'abord : elles sont SUPPOSÉES (P1 arrive après). */
/* ---------------------------------------------------------------------- */

/** `POST …/eleves/:eleveId/parcours` et `…/cohortes/:cohorteId/parcours` : le parcours et le mot du tuteur. */
export type DeposerParcoursTuteurBody = CreerParcoursBody & { readonly mot?: string };

/** `POST …/parcours/ebauche` (P4) — une proposition, rien n'est écrit. */
export interface EbaucheParcoursBody {
  readonly matiere: Matiere;
  readonly chapitres: readonly string[];
  readonly minutes: number;
  readonly pourLe?: string;
}

export interface EbaucheParcoursResponse {
  readonly nom: string;
  readonly blocs: readonly BlocParcoursBody[];
  /** La raison de chaque étape, par `genre:code`. */
  readonly raisons: Readonly<Record<string, string>>;
}

/** Un élève de la classe, devant UN parcours déposé : « 3 sur 7 », en retard ou non. */
export interface EleveDansParcoursDeClasse {
  readonly eleveId: string;
  readonly prenom: string;
  /** Son exemplaire du parcours (un par élève de la classe). */
  readonly parcoursId: string;
  readonly etat: EtatParcours;
  readonly faites: number;
  readonly total: number;
  readonly pourLe: string | null;
  readonly enRetard: boolean;
}

/** Un dépôt à la classe : son nom, sa date, et une ligne par élève. */
export interface ParcoursDeClasse {
  /** L'identifiant du dépôt — le même pour tous les exemplaires. */
  readonly id: string;
  readonly nom: string;
  readonly matiere: Matiere;
  readonly creeLe: string;
  readonly pourLe: string | null;
  readonly total: number;
  readonly eleves: readonly EleveDansParcoursDeClasse[];
}

/** `GET /v1/tuteur/manuel/cohortes/:cohorteId/parcours` — le suivi par classe, le plus récent d'abord. */
export interface SuiviParcoursClasseResponse {
  readonly parcours: readonly ParcoursDeClasse[];
}

/**
 * La route d'ébauche se sonde avant d'afficher « Proposer une ébauche » : tant
 * qu'elle répond 404 (P4 pas encore servi), le bouton reste masqué. Le verdict
 * se retient le temps de la page : une sonde par chargement, pas une par écran.
 */
let ebaucheServie: Promise<boolean> | null = null;

/** Oublier le verdict de la sonde — pour les tests, et après un déploiement de P4. */
export function oublierSondeEbauche(): void {
  ebaucheServie = null;
}

/** Surface typée de l'API Silex utilisée par la console. */
/** La classe telle que la console l'affiche — miroir de `ClasseDto` (contrat partagé). */
export interface MembreVue {
  readonly eleveId: string;
  readonly prenom: string;
  readonly niveauScolaire: NiveauScolaire;
}

export interface ClasseVue {
  readonly id: string;
  readonly nom: string;
  readonly debut: string | null;
  readonly orgId: string | null;
  readonly membres: readonly MembreVue[];
  readonly nbMembres: number;
}

export interface ClassesReponse {
  readonly classes: readonly ClasseVue[];
  readonly nbClasses: number;
}

/**
 * AB#884 — le jeton du défi anti-robot, quand le widget en a délivré un
 * (`auth/DefiAntiRobot.tsx`). Sans clé de site : le corps tel quel.
 */
async function avecDefi<T extends object>(corps: T): Promise<T> {
  const jeton = await prendreJeton();
  return jeton === undefined ? corps : { ...corps, [CHAMP_DEFI_ANTI_ROBOT]: jeton };
}

export const api = {
  /** Connexion d'un compte titulaire (tuteur / admin). `otp` si MFA actif. */
  login: async (input: LoginRequest) =>
    request<LoginResponse>('/v1/auth/login', {
      method: 'POST',
      body: await avecDefi(input),
    }),

  /**
   * AB#296 — étape 1 de l'enrôlement du double facteur : un secret neuf, rendu
   * une seule fois, et son URI `otpauth://` à montrer en QR code.
   */
  demarrerEnrolementMfa: () =>
    request<MfaEnrolementResponse>('/v1/auth/mfa/enrolement', { method: 'POST', auth: true }),

  /**
   * AB#296 — étape 2 : le premier code de l'application active le double
   * facteur. L'API révoque toutes les sessions antérieures et en rend une
   * neuve : l'appelant DOIT remplacer ses jetons par ceux-ci.
   */
  confirmerEnrolementMfa: (otp: string) =>
    request<MfaConfirmationResponse>('/v1/auth/mfa/confirmation', {
      method: 'POST',
      body: { otp },
      auth: true,
    }),

  /** GET /v1/auth/moi — l'état du compte connecté : courriel, rôle, double facteur. */
  moi: () => request<AuthenticatedUser & { courriel: string }>('/v1/auth/moi', { auth: true }),

  /**
   * AB#747 — changer son mot de passe une fois connecté. L'API révoque TOUTES
   * les sessions du compte et en rend une neuve : l'appelant DOIT remplacer
   * ses jetons par ceux-ci (`MonComptePage`).
   */
  changerMotDePasse: (input: ChangerMotDePasseRequest) =>
    request<ChangerMotDePasseResponse>('/v1/auth/mot-de-passe', {
      method: 'POST',
      body: input,
      auth: true,
    }),

  /**
   * AB#747 — « Mot de passe oublié ? » depuis l'écran de connexion. Réponse
   * CONSTANTE (202 `{ envoye: true }`), que le compte existe ou non.
   */
  motDePasseOublie: async (email: string) =>
    request<{ envoye: true }>('/v1/auth/mot-de-passe-oublie', {
      method: 'POST',
      body: await avecDefi({ email }),
    }),

  /**
   * Échange le refresh token courant contre une nouvelle paire (rotation).
   * `true` si la session est reprise, `false` sinon. Utilisé au démarrage.
   */
  refresh: (): Promise<boolean> => renouvelerUneFois(),

  /** Révoque le refresh token côté API (best-effort), puis vide le stockage. */
  logout: async (): Promise<void> => {
    const refreshToken = tokenStore.getRefreshToken();
    if (refreshToken) {
      try {
        await request<void>('/v1/auth/logout', { method: 'POST', body: { refreshToken } });
      } catch {
        /* session fermée localement quoi qu'il arrive */
      }
    }
    tokenStore.clear();
  },

  /**
   * « Qui suis-je » — dérivé de l'access token en mémoire (décodage local, cf.
   * `decodeClaims`). Renvoie `null` si aucun jeton valide.
   */
  me: (): AccessTokenClaims | null => {
    const token = tokenStore.getAccessToken();
    return token ? decodeClaims(token) : null;
  },

  /**
   * Suivi (SLX-078, Sprint 12) — agrégats de la fiche élève : progression par
   * savoir, dose de travail, évaluations diagnostiques, recommandations.
   * Lecture seule, bornée à l'élève côté API (`modules/suivi`).
   */
  getProgression: (eleveId: string) =>
    request<ProgressionEleveResponse>(`/v1/suivi/${eleveId}/progression`, { auth: true }),

  getDose: (eleveId: string, periode: PeriodeDose = 'semaine') =>
    request<DoseEleveResponse>(`/v1/suivi/${eleveId}/dose?periode=${periode}`, { auth: true }),

  getEvaluationsDiagnostiques: (eleveId: string) =>
    request<EvaluationsEleveResponse>(`/v1/suivi/${eleveId}/evaluations`, { auth: true }),

  getRecommandations: (eleveId: string) =>
    request<RecommandationsEleveResponse>(`/v1/suivi/${eleveId}/recommandations`, { auth: true }),

  /**
   * Le JOURNAL d'un savoir — les faits datés, du plus récent au plus ancien
   * (G4 : « les trois dernières figures d'Émile »).
   *
   * Chaque entrée porte la SPÉCIFICATION BORNÉE de la figure qui a été montrée
   * et la lecture déclarée par l'élève. Il n'y a **aucune image à stocker ni à
   * transmettre** : la figure est reconstruite au rendu. Ces faits ne sont
   * jamais agrégés entre eux — c'est la série que le tuteur lit, pas un
   * décompte « 2 sur 3 ».
   */
  getObservations: (eleveId: string, savoirId: string) =>
    request<ObservationsSavoirResponse>(
      `/v1/suivi/${eleveId}/observations?savoirId=${encodeURIComponent(savoirId)}`,
      { auth: true },
    ),

  /** Plan d'apprentissage de référence courant (le plus récent `approuve`), ou `null`. */
  getPlanActif: (eleveId: string) =>
    request<PlanActifResponse>(`/v1/enfants/${eleveId}/plan-actif`, { auth: true }),

  /** Rapport de synthèse — lien depuis une évaluation diagnostique déjà analysée. */
  getRapport: (rapportId: string) =>
    request<RapportEvaluationDto>(`/v1/rapports/${rapportId}`, { auth: true }),

  /* ---------------------------------------------------------------------- */
  /* Console tuteur (Sprint 16, PRD Module 10) — `/v1/tuteur/*`.            */
  /*                                                                        */
  /* Aucune de ces routes ne prend d'identifiant d'élève dans l'URL sauf    */
  /* `aides-devoirs` : le périmètre est lu depuis le jeton côté API         */
  /* (`principal.sub` → affectations `fin: null`). Rien à falsifier ici.    */
  /* Toutes sont réservées au rôle `tuteur` (403 sinon) — sauf la revue,    */
  /* ouverte à `admin` pour l'exploitation.                                 */
  /* ---------------------------------------------------------------------- */

  /** T-1 — la file de travail du matin, déjà priorisée et bornée par le serveur. */
  getFileTuteur: () => request<FileTuteurResponse>('/v1/tuteur/file', { auth: true }),

  /** « Plus tard » — l'unité quitte la file du jour et revient à `reprendLe`. */
  reporterUnite: (input: ReporterUniteRequest) =>
    request<ReporterUniteResponse>('/v1/tuteur/file/reporter', {
      method: 'POST',
      body: input,
      auth: true,
    }),

  /** Annule un report avant son échéance : l'unité revient dans la file du jour. */
  reprendreUnite: (input: ReprendreUniteRequest) =>
    request<void>('/v1/tuteur/file/reprendre', { method: 'POST', body: input, auth: true }),

  /** Les élèves suivis — paniers dénombrés, jamais de niveau agrégé. */
  getElevesDuTuteur: () => request<ElevesDuTuteurResponse>('/v1/tuteur/eleves', { auth: true }),

  /** T-2 — la file de validation, avec la durée totale annoncée. */
  getEvaluationsAValider: () =>
    request<EvaluationsAValiderResponse>('/v1/tuteur/evaluations', { auth: true }),

  /**
   * T-2 — confirmer ou corriger un palier. `corrige` exige `niveauMaitrise`
   * (un des cinq paliers NOMMÉS) et `motif` ; l'API refuse tout nombre.
   */
  revoirEvaluation: (evaluationId: string, input: RevueEvaluationRequest) =>
    request<RevueEvaluationResponse>(`/v1/evaluations/${evaluationId}/revue`, {
      method: 'POST',
      body: input,
      auth: true,
    }),

  /** T-3 — historique d'aides aux devoirs : le meilleur signal de blocage. */
  getAidesDevoirs: (eleveId: string) =>
    request<AidesDevoirsEleveResponse>(`/v1/tuteur/eleves/${eleveId}/aides-devoirs`, {
      auth: true,
    }),

  /**
   * `GET /v1/tuteur/eleves/:id/copies` — les travaux corrigés d'un élève.
   *
   * Le fondateur, en recette : « impossible de charger les devoirs de la
   * section apprentissage annotés pour les visualiser. On voit juste la
   * liste. » La console n'avait aucune route vers les copies — celle de l'élève
   * garde par appartenance, et l'élargir aurait ouvert les travaux d'un enfant
   * à quiconque porte un jeton. Le périmètre du tuteur tient à une affectation
   * active, vérifiée côté serveur.
   */
  getCopiesEleve: (eleveId: string, matiere?: 'francais' | 'mathematiques') =>
    request<CopiesEleveResponse>(
      `/v1/tuteur/eleves/${eleveId}/copies${matiere ? `?matiere=${matiere}` : ''}`,
      { auth: true },
    ),

  /** `GET /v1/tuteur/eleves/:id/copies/:copieId` — LA copie annotée. */
  getCopieEleve: (eleveId: string, copieId: string) =>
    request<CopieDto>(`/v1/tuteur/eleves/${eleveId}/copies/${copieId}`, { auth: true }),

  /* ---------------------------------------------------------------------- */
  /* Administration (Sprint 14, PRD Module 15) — `/v1/admin/*`.             */
  /* Toutes ces routes sont réservées au rôle `admin` côté API              */
  /* (`requireRole(['admin'])`) : un appel avec un autre rôle répond 403,   */
  /* la garde `RequireAuth adminOnly` du client n'est qu'un confort.        */
  /* ---------------------------------------------------------------------- */

  /** Crée un compte tuteur. `201` → le compte créé, jamais le mot de passe. */
  creerTuteur: (input: CreerCompteAdminRequest) =>
    request<CompteAdminDto>('/v1/admin/tuteurs', { method: 'POST', body: input, auth: true }),

  /** Crée un compte parent — même corps que `creerTuteur`, la route porte le rôle. */
  creerParent: (input: CreerCompteAdminRequest) =>
    request<CompteAdminDto>('/v1/admin/parents', { method: 'POST', body: input, auth: true }),

  /** Ajoute un enfant au parent désigné par `parentId`. */
  creerEnfant: (input: CreerEnfantAdminRequest) =>
    request<ChildProfile>('/v1/admin/enfants', { method: 'POST', body: input, auth: true }),

  /**
   * AB#296 — récupération : efface le double facteur d'un AUTRE compte et
   * révoque ses sessions. Journalisé côté API (piste d'audit, SEC-17).
   */
  reinitialiserMfa: (userId: string) =>
    request<ReinitialisationMfaResponse>(
      `/v1/admin/comptes/${encodeURIComponent(userId)}/mfa/reinitialiser`,
      { method: 'POST', auth: true },
    ),

  /**
   * Liste/recherche paginée des comptes. `q` cherche e-mail et nom.
   * `inclureTests` (D-7, 15 oct. 2026) : le serveur MASQUE les comptes de test
   * par défaut ; `true` les rend (`?inclureTests=1`). Un serveur d'avant
   * ignore le paramètre et rend tout, comme avant.
   */
  listComptes: (
    params: { role?: AccountRole; q?: string; cursor?: string; inclureTests?: boolean } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.role) qs.set('role', params.role);
    if (params.q) qs.set('q', params.q);
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.inclureTests === true) qs.set('inclureTests', '1');
    return request<ListeComptesResponse>(`/v1/admin/comptes${suffixe(qs)}`, { auth: true });
  },

  /**
   * Activité de connexion (comptes titulaires + sous-profils élèves).
   *
   * ATTENTION : le paramètre de borne haute s'écrit littéralement `jusqu-a`
   * (avec un tiret), pas `jusqua` — cf. `admin.routes.ts`, `connexionsQuerySchema`.
   * La réponse, elle, renvoie bien `jusqua`. Fenêtre par défaut si omis : 30 jours.
   */
  listConnexions: (params: { depuis?: string; jusquA?: string; cursor?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.depuis) qs.set('depuis', params.depuis);
    if (params.jusquA) qs.set('jusqu-a', params.jusquA);
    if (params.cursor) qs.set('cursor', params.cursor);
    return request<ListeConnexionsResponse>(`/v1/admin/usage/connexions${suffixe(qs)}`, {
      auth: true,
    });
  },

  /**
   * Consommation IA agrégée. `groupBy` ne choisit que la vue mise en avant :
   * `parJour` ET `parAction` sont TOUJOURS renvoyés tous les deux.
   * Même piège de nommage que ci-dessus sur `jusqu-a`.
   */
  getUsageIa: (params: { depuis?: string; jusquA?: string; groupBy?: GroupeUsageIa } = {}) => {
    const qs = new URLSearchParams();
    if (params.depuis) qs.set('depuis', params.depuis);
    if (params.jusquA) qs.set('jusqu-a', params.jusquA);
    if (params.groupBy) qs.set('groupBy', params.groupBy);
    return request<UsageIaResponse>(`/v1/admin/usage/ia${suffixe(qs)}`, { auth: true });
  },

  /**
   * `GET /v1/admin/retroaction/indicateurs` — les indicateurs de la rétroaction
   * par semaine (P1-J, AB#876) : comptes et taux, pour le fondateur seul.
   */
  indicateursRetroaction: (semaines?: number) =>
    request<IndicateursRetroactionResponse>(
      `/v1/admin/retroaction/indicateurs${semaines !== undefined ? `?semaines=${semaines}` : ''}`,
      { auth: true },
    ),

  /** AB#884 — `GET /v1/admin/anti-robots` : comptes signalés, blocages, compteurs du défi. */
  etatAntiRobots: () => request<EtatAntiRobotsResponse>('/v1/admin/anti-robots', { auth: true }),

  /** AB#884 — lever les blocages de lecture en cours d'un compte. */
  leverBlocageLecture: (compteId: string) =>
    request<LeveeBlocageResponse>(
      `/v1/admin/anti-robots/comptes/${encodeURIComponent(compteId)}/levee`,
      { method: 'POST', auth: true },
    ),

  /** Tous les élèves : dose de la semaine + synthèse de progression. */
  listElevesAdmin: (cursor?: string) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    return request<ListeElevesAdminResponse>(`/v1/admin/eleves${suffixe(qs)}`, { auth: true });
  },

  /**
   * ACCORDER UN QUOTA DE CRÉDITS À UN FOYER — AB#632.
   *
   * `compteId` est l'identifiant du TITULAIRE, jamais celui d'un sous-profil
   * élève : le portefeuille appartient au foyer. `EleveAdminDto.parentId` est
   * donc ce qu'il faut passer, et c'est ce que la console lit.
   *
   * Le montant n'est pas un paramètre : la route refuse un champ numérique
   * libre et n'accepte qu'un quota du catalogue — `essai` (7 500) ou
   * `abonnement` (100 000). Lui donner un nombre ici rendrait au robinet la
   * poignée qu'on lui a retirée.
   */
  accorderQuota: (compteId: string, quota: 'essai' | 'abonnement') =>
    request<OctroiQuotaResponse>('/v1/admin/credits', {
      method: 'POST',
      body: { compteId, quota },
      auth: true,
    }),

  /* ---------------------------------------------------------------------- */
  /* Chantier « manuel vivant » — le réglage de la capacité (MV-SOC-01).     */
  /* ---------------------------------------------------------------------- */

  /** `GET /v1/admin/capacites` — toutes les organisations, et les foyers allumés. */
  etatCapacites: () => request<EtatCapacitesResponse>('/v1/admin/capacites', { auth: true }),

  /**
   * `PUT /v1/admin/foyers/:id/capacites` ou
   * `PUT /v1/admin/organisations/:id/capacites` — allumer ou éteindre le
   * manuel vivant. `id` : le compte titulaire du foyer, ou l'organisation.
   * Idempotent.
   */
  reglerManuelVivant: (cible: 'foyer' | 'organisation', id: string, manuelVivant: boolean) =>
    request<ReglerCapacitesResponse>(
      `/v1/admin/${cible === 'foyer' ? 'foyers' : 'organisations'}/${encodeURIComponent(id)}/capacites`,
      { method: 'PUT', body: { manuelVivant }, auth: true },
    ),

  /* ---------------------------------------------------------------------- */
  /* Chantier « manuel vivant » — le programme (Φ-MV-1, MV-CNT-06).          */
  /*                                                                        */
  /* Deux lectures, pour la page « Programme ». Tuteur ET admin, derrière la */
  /* capacité : sans elle le serveur répond 403 `MANUEL_VIVANT_INACTIF`, et  */
  /* la page n'a de toute façon ni entrée de menu ni route.                  */
  /* ---------------------------------------------------------------------- */

  /** `GET /v1/tuteur/manuel/chapitres` — tous les chapitres, publiés ou non. */
  programmeChapitres: (options: { readonly relecture?: boolean } = {}) =>
    request<ChapitresProgrammeResponse>(
      /* L2 — `?relecture=1` : les comptes de relecture de chaque chapitre,
         exercices compris (champ `relecture`, absent d'un serveur d'avant). */
      `/v1/tuteur/manuel/chapitres${options.relecture === true ? '?relecture=1' : ''}`,
      { auth: true },
    ),

  /** `GET /v1/tuteur/manuel/chapitres/:code` — un chapitre, tel que la console le relit. */
  programmeChapitre: (code: string) =>
    request<ChapitreProgrammeResponse>(`/v1/tuteur/manuel/chapitres/${encodeURIComponent(code)}`, {
      auth: true,
    }),

  /* ---------------------------------------------------------------------- */
  /* Chantier « manuel vivant » — la base de savoir (Φ-MV-2, MV-CNT-05, 06). */
  /*                                                                        */
  /* Trois lectures et deux écritures d'ÉTAT, pour la relecture d'une fiche, */
  /* d'une capsule, d'une erreur et de son remède. La console n'écrit aucun  */
  /* CONTENU : une fiche se corrige dans son dossier, puis par l'import.     */
  /* Mêmes rôles et même garde que les lectures du programme.                */
  /* ---------------------------------------------------------------------- */

  /** `GET /v1/tuteur/manuel/chapitres/:code/savoir` — fiches, capsules, erreurs et remèdes. */
  savoirChapitre: (code: string) =>
    request<SavoirChapitreResponse>(
      `/v1/tuteur/manuel/chapitres/${encodeURIComponent(code)}/savoir`,
      { auth: true },
    ),

  /** `GET /v1/tuteur/manuel/fiches/:code` — la fiche, telle que l'élève la lit, et son état. */
  ficheRelecture: (code: string) =>
    request<FicheRelectureResponse>(`/v1/tuteur/manuel/fiches/${encodeURIComponent(code)}`, {
      auth: true,
    }),

  /** `GET /v1/tuteur/manuel/capsules/:code` — la capsule, ses médias, sa provenance, son état. */
  capsuleRelecture: (code: string) =>
    request<CapsuleRelectureResponse>(`/v1/tuteur/manuel/capsules/${encodeURIComponent(code)}`, {
      auth: true,
    }),

  /**
   * `POST /v1/tuteur/manuel/{fiches|capsules|erreurs}/:code/relecture` —
   * « Relu, rien à changer ». Sans corps : l'acte est dans l'adresse.
   */
  marquerSavoirRelu: (sorte: SorteSavoir, code: string) =>
    request<RelectureSavoirResponse>(
      `/v1/tuteur/manuel/${sorte}/${encodeURIComponent(code)}/relecture`,
      { method: 'POST', auth: true },
    ),

  /**
   * `DELETE /v1/tuteur/manuel/{fiches|capsules|erreurs}/:code/relecture` —
   * « Remettre à relire » : défait un « relu » posé par mégarde.
   */
  remettreSavoirARelire: (sorte: SorteSavoir, code: string) =>
    request<RelectureSavoirResponse>(
      `/v1/tuteur/manuel/${sorte}/${encodeURIComponent(code)}/relecture`,
      { method: 'DELETE', auth: true },
    ),

  /**
   * `POST /v1/tuteur/manuel/{fiches|capsules|chapitres}/:code/statut` — sortir
   * du service, valider, publier. Un refus (transition non admise, chapitre
   * dont une erreur n'a pas son ancre) revient en 409 : `ApiError.message`
   * porte la raison, à afficher telle quelle.
   */
  changerStatutSavoir: (
    sorte: Exclude<SorteSavoir, 'erreurs'> | 'chapitres',
    code: string,
    body: ChangerStatutSavoirBody,
  ) =>
    request<StatutSavoirResponse>(`/v1/tuteur/manuel/${sorte}/${encodeURIComponent(code)}/statut`, {
      method: 'POST',
      body,
      auth: true,
    }),

  /* ---------------------------------------------------------------------- */
  /* Chantier « manuel vivant » — le tuteur (Φ-MV-5 : MV-TUT-02, 03, 05,     */
  /* 06, 12).                                                                */
  /*                                                                        */
  /* Contrat : `@silex/shared`, `manuel-tuteur.ts`. Un tuteur ne lit et      */
  /* n'écrit que pour un élève QU'IL SUIT (403 sinon) ; l'administrateur     */
  /* lit, n'écrit rien (`VueEleveTuteurResponse.peutEcrire`). Un refus de    */
  /* règle revient en 422, sa raison en une phrase dans `ApiError.message` : */
  /* elle s'affiche telle quelle. Appelants : `pages/ManuelElevePage.tsx`,   */
  /* `pages/ComposerSequencePage.tsx` et `pages/manuel-eleve/`.              */
  /* ---------------------------------------------------------------------- */

  /** `GET /v1/tuteur/manuel/eleves/:eleveId` — la vue d'un élève pour son tuteur, en une lecture. */
  manuelVueEleve: (eleveId: string) =>
    request<VueEleveTuteurResponse>(`/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}`, {
      auth: true,
    }),

  /** `POST /v1/tuteur/manuel/eleves/:eleveId/objectif` — poser l'objectif ; un actif de la même matière est clos. */
  manuelPoserObjectif: (eleveId: string, body: PoserObjectifBody) =>
    request<ObjectifResponse>(`/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/objectif`, {
      method: 'POST',
      body,
      auth: true,
    }),

  /** `PATCH /v1/tuteur/manuel/objectifs/:id` — le modifier, sans réécrire son auteur d'origine. */
  manuelModifierObjectif: (objectifId: string, body: ModifierObjectifBody) =>
    request<ObjectifResponse>(`/v1/tuteur/manuel/objectifs/${encodeURIComponent(objectifId)}`, {
      method: 'PATCH',
      body,
      auth: true,
    }),

  /** `PUT /v1/tuteur/manuel/eleves/:eleveId/niveau-maximal` — `null` lève le réglage : tout est ouvert. */
  manuelReglerNiveauMaximal: (eleveId: string, body: ReglerNiveauMaximalBody) =>
    request<NiveauMaximalResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/niveau-maximal`,
      { method: 'PUT', body, auth: true },
    ),

  /** `GET /v1/tuteur/manuel/eleves/:eleveId/sequences` — toutes, retirées comprises, la plus récente d'abord. */
  manuelSequences: (eleveId: string) =>
    request<SequencesTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/sequences`,
      { auth: true },
    ),

  /** `POST /v1/tuteur/manuel/eleves/:eleveId/sequences` — déposer une séquence dans la liste de l'élève. */
  manuelDeposerSequence: (eleveId: string, body: DeposerSequenceBody) =>
    request<SequenceTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/sequences`,
      { method: 'POST', body, auth: true },
    ),

  /** `PATCH /v1/tuteur/manuel/sequences/:id` — `elements`, s'il est donné, remplace la liste dans l'ordre. */
  manuelModifierSequence: (sequenceId: string, body: ModifierSequenceBody) =>
    request<SequenceTuteurResponse>(
      `/v1/tuteur/manuel/sequences/${encodeURIComponent(sequenceId)}`,
      { method: 'PATCH', body, auth: true },
    ),

  /** `DELETE /v1/tuteur/manuel/sequences/:id` — la RETIRER de la liste : archivée, jamais supprimée. */
  manuelRetirerSequence: (sequenceId: string) =>
    request<SequenceTuteurResponse>(
      `/v1/tuteur/manuel/sequences/${encodeURIComponent(sequenceId)}`,
      { method: 'DELETE', auth: true },
    ),

  /* ---------------------------------------------------------------------- */
  /* Le parcours, côté tuteur (P3, AB#975). Les routes de séquences d'au-    */
  /* dessus ne changent pas : un parcours EST une séquence élargie, et sa    */
  /* retouche à plat ou son retrait passent encore par elles. Appelants :    */
  /* `pages/ComposerParcoursPage.tsx`, `pages/SuiviParcoursClassePage.tsx`,  */
  /* `pages/manuel-eleve/SequencesEleve.tsx`.                                */
  /* ---------------------------------------------------------------------- */

  /**
   * `GET /v1/tuteur/manuel/eleves/:eleveId/parcours` — ses parcours, vus par
   * le tuteur. Le contrat dit un tableau ; une enveloppe `{ parcours }` est
   * lue aussi, pour ne pas casser sur la forme.
   */
  manuelParcoursEleve: async (eleveId: string): Promise<readonly ParcoursEleveDto[]> => {
    const r = await request<
      readonly ParcoursEleveDto[] | { readonly parcours: readonly ParcoursEleveDto[] }
    >(`/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/parcours`, { auth: true });
    return Array.isArray(r)
      ? r
      : (r as { readonly parcours: readonly ParcoursEleveDto[] }).parcours;
  },

  /** `POST /v1/tuteur/manuel/eleves/:eleveId/parcours` — déposer un parcours dans la liste d'un élève. */
  manuelDeposerParcoursEleve: (eleveId: string, body: DeposerParcoursTuteurBody) =>
    request<ParcoursEleveDto>(`/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/parcours`, {
      method: 'POST',
      body,
      auth: true,
    }),

  /** `POST /v1/tuteur/manuel/cohortes/:cohorteId/parcours` — un parcours par élève de la classe. */
  manuelDeposerParcoursClasse: (cohorteId: string, body: DeposerParcoursTuteurBody) =>
    request<{ readonly deposes: number }>(
      `/v1/tuteur/manuel/cohortes/${encodeURIComponent(cohorteId)}/parcours`,
      { method: 'POST', body, auth: true },
    ),

  /** `GET /v1/tuteur/manuel/cohortes/:cohorteId/parcours` — chaque dépôt, une ligne par élève. */
  manuelSuiviParcoursClasse: (cohorteId: string) =>
    request<SuiviParcoursClasseResponse>(
      `/v1/tuteur/manuel/cohortes/${encodeURIComponent(cohorteId)}/parcours`,
      { auth: true },
    ),

  /** `POST /v1/tuteur/manuel/eleves/:eleveId/parcours/ebauche` (P4) — une proposition, rien d'écrit. */
  manuelEbaucheParcours: (eleveId: string, body: EbaucheParcoursBody) =>
    request<EbaucheParcoursResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/parcours/ebauche`,
      { method: 'POST', body, auth: true },
    ),

  /**
   * La route d'ébauche est-elle servie ? Un corps vide : un serveur qui la
   * connaît le refuse (400 ou 422), un serveur d'avant P4 répond 404. Une
   * panne réseau compte pour « non » : le bouton n'apparaît pas pour échouer.
   */
  manuelEbaucheServie: (eleveId: string): Promise<boolean> => {
    ebaucheServie =
      ebaucheServie ??
      request<unknown>(`/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/parcours/ebauche`, {
        method: 'POST',
        body: {},
        auth: true,
      }).then(
        () => true,
        (err: unknown) => err instanceof ApiError && err.status !== 404,
      );
    return ebaucheServie;
  },

  /** `GET /v1/tuteur/manuel/eleves/:eleveId/seances` — de la plus récente à la plus ancienne. */
  manuelSeances: (eleveId: string) =>
    request<SeancesTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/seances`,
      { auth: true },
    ),

  /** `POST /v1/tuteur/manuel/eleves/:eleveId/seances` — planifier une séance, ou la consigner faite. */
  manuelOuvrirSeance: (eleveId: string, body: OuvrirSeanceBody) =>
    request<SeanceTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/seances`,
      { method: 'POST', body, auth: true },
    ),

  /** `PATCH /v1/tuteur/manuel/seances/:id` — la clore avec sa note, ou la corriger. */
  manuelNoterSeance: (seanceId: string, body: NoterSeanceBody) =>
    request<SeanceTuteurResponse>(`/v1/tuteur/manuel/seances/${encodeURIComponent(seanceId)}`, {
      method: 'PATCH',
      body,
      auth: true,
    }),

  /**
   * `GET /v1/tuteur/manuel/chapitres/:code/exercices` — les exercices du
   * chapitre, publiés ou non : la banque où le tuteur choisit ceux d'une
   * séquence (seuls les publiés sont proposés à l'ajout).
   */
  manuelExercicesChapitre: (code: string) =>
    request<ExercicesChapitreResponse>(
      `/v1/tuteur/manuel/chapitres/${encodeURIComponent(code)}/exercices`,
      { auth: true },
    ),

  /* ---------------------------------------------------------------------- */
  /* Refonte de la console, L0 et L3 — relire un exercice du manuel.         */
  /* Une lecture (avec la réponse) et deux écritures d'ÉTAT : « relu », « à  */
  /* corriger » avec une note (D-1 : jamais une édition), et leur remise.    */
  /* Tuteur ET admin (D-4). Appelant : `pages/RelectureExercicesPage.tsx`.   */
  /* ---------------------------------------------------------------------- */

  /** `GET /v1/tuteur/manuel/exercices/:code` — l'exercice, sa réponse, ses réponses fautives, sa démarche. */
  exerciceRelecture: (code: string) =>
    request<ExerciceRelectureResponse>(`/v1/tuteur/manuel/exercices/${encodeURIComponent(code)}`, {
      auth: true,
    }),

  /** `POST /v1/tuteur/manuel/exercices/:code/relecture` — « relu », ou « à corriger » avec sa note. */
  relireExercice: (code: string, body: RelectureExerciceBody) =>
    request<RelectureExerciceResponse>(
      `/v1/tuteur/manuel/exercices/${encodeURIComponent(code)}/relecture`,
      { method: 'POST', body, auth: true },
    ),

  /** `DELETE /v1/tuteur/manuel/exercices/:code/relecture` — remettre à relire. */
  remettreExerciceARelire: (code: string) =>
    request<RelectureExerciceResponse>(
      `/v1/tuteur/manuel/exercices/${encodeURIComponent(code)}/relecture`,
      { method: 'DELETE', auth: true },
    ),

  /** `POST /v1/tuteur/manuel/exercices/:code/retrait` — ADMIN : retirer du service, motif obligatoire. */
  retirerExercice: (code: string, motif: string) =>
    request<RetraitExerciceResponse>(
      `/v1/tuteur/manuel/exercices/${encodeURIComponent(code)}/retrait`,
      { method: 'POST', body: { motif }, auth: true },
    ),

  /** `DELETE /v1/tuteur/manuel/exercices/:code/retrait` — ADMIN : remettre en service. */
  remettreExerciceEnService: (code: string) =>
    request<RetraitExerciceResponse>(
      `/v1/tuteur/manuel/exercices/${encodeURIComponent(code)}/retrait`,
      { method: 'DELETE', auth: true },
    ),

  /* ---------------------------------------------------------------------- */
  /* Chantier « manuel vivant » — le tuteur, seconde tranche (Φ-MV-5 :       */
  /* MV-TUT-01, 04, 07, 08, 11). Cinq LECTURES calculées par le serveur ;    */
  /* aucune n'écrit, aucune n'appelle de modèle. Appelants :                 */
  /* `pages/mes-eleves/ElevesParUrgence.tsx`, `pages/PreparationSeancePage`, */
  /* `pages/manuel-eleve/TravauxEleve.tsx`, `…/EvolutionEleve.tsx`,          */
  /* `pages/RapportUniquePage.tsx`.                                          */
  /* ---------------------------------------------------------------------- */

  /** `GET /v1/tuteur/manuel/eleves` — les élèves suivis, dans l'ordre où les voir, chacun avec sa raison. */
  manuelElevesSuivis: () =>
    request<ElevesSuivisResponse>('/v1/tuteur/manuel/eleves', { auth: true }),

  /** `GET /v1/tuteur/manuel/eleves/:eleveId/preparation-seance` — quoi travailler, dans quel ordre, avec quel appui. */
  manuelPreparationSeance: (eleveId: string) =>
    request<PreparationSeanceResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/preparation-seance`,
      { auth: true },
    ),

  /**
   * `GET /v1/tuteur/manuel/eleves/:eleveId/travaux` — essais, défis et devoirs
   * réunis, du plus récent au plus ancien. Les filtres se cumulent ; un filtre
   * absent (ou vide) n'est pas envoyé.
   */
  /**
   * `GET …/travaux/:copieId/pages` — les pages au stylet d'un essai, telles
   * que l'élève les a écrites (tranche T2). Lues À LA DEMANDE : quatre images
   * par essai ne voyagent pas avec la liste des travaux.
   */
  manuelPagesDeTravail: (eleveId: string, copieId: string) =>
    request<PagesDeTravailTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/travaux/${encodeURIComponent(copieId)}/pages`,
      { auth: true },
    ),
  /**
   * Le retour qu'un travail a rendu à l'élève, dans la VUE TUTEUR du gabarit
   * unique (chantier « Rétroaction », P1-E) : un document HTML autonome, sans
   * script, que la console pose dans un cadre isolé. 404 `RETROACTION_ABSENTE` :
   * le travail n'en a pas.
   */
  manuelRetroactionDuTravail: (eleveId: string, copieId: string) =>
    request<string>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/travaux/${encodeURIComponent(copieId)}/retroaction`,
      { auth: true, texte: true },
    ),
  /**
   * Atelier de figures, L7 — LES NOTES DU TUTEUR sur un essai à tracer (option
   * B) : une phrase ancrée à un objet du tracé, un constat épinglé peut-être.
   * Sans dessin, sans effet sur le verdict. Appelant : `NotesDuTuteurSurLeTrace`
   * (`pages/manuel-eleve/TravauxEleve.tsx`).
   */
  manuelNotesDuTravail: (eleveId: string, copieId: string) =>
    request<NotesDuTravailResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/travaux/${encodeURIComponent(copieId)}/notes`,
      { auth: true },
    ),
  ecrireNoteDuTuteur: (eleveId: string, copieId: string, corps: EcrireNoteDuTuteurBody) =>
    request<NoteDuTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/travaux/${encodeURIComponent(copieId)}/notes`,
      { method: 'POST', body: corps, auth: true },
    ),
  retirerNoteDuTuteur: (eleveId: string, copieId: string, noteId: string) =>
    request<NoteDuTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/travaux/${encodeURIComponent(copieId)}/notes/${encodeURIComponent(noteId)}`,
      { method: 'DELETE', auth: true },
    ),
  manuelTravaux: (eleveId: string, filtres: FiltresTravaux = {}) => {
    const q = new URLSearchParams();
    for (const cle of ['genre', 'notion', 'verdict', 'erreur'] as const) {
      const valeur = filtres[cle];
      if (valeur) q.set(cle, valeur);
    }
    const suite = q.toString();
    return request<TravauxTuteurResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/travaux${suite ? `?${suite}` : ''}`,
      { auth: true },
    );
  },

  /** `GET /v1/tuteur/manuel/eleves/:eleveId/evolution` — cinq semaines : des comptes, et le palier de chaque notion. */
  manuelEvolution: (eleveId: string) =>
    request<EvolutionEleveResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/evolution`,
      { auth: true },
    ),

  /** `GET /v1/tuteur/manuel/eleves/:eleveId/rapport` — LE rapport : le dernier que la famille a reçu, ou `null`. */
  manuelRapport: (eleveId: string) =>
    request<RapportUniqueResponse>(
      `/v1/tuteur/manuel/eleves/${encodeURIComponent(eleveId)}/rapport`,
      { auth: true },
    ),

  /* ---------------------------------------------------------------------- */
  /* LOT 3.4 — affectation tuteur ↔ élève (CON-ADM-CRE-02, CON-ADM-CRE-03).  */
  /*                                                                        */
  /* Les trois routes existaient et répondaient ; la console ne les appelait */
  /* nulle part. La campagne a dû les exercer HORS interface pour établir le */
  /* fait — « la capacité existe pourtant ». Un tuteur créé depuis la console */
  /* restait donc sans élève, sur un état vide qui renvoie à « un            */
  /* administrateur de l'organisation », c'est-à-dire à la personne devant   */
  /* l'écran, qui n'avait pas l'outil.                                       */
  /*                                                                        */
  /* Réservées au rôle `admin` côté API (`assertAdmin`), et bornées à une    */
  /* organisation : `orgId` est le `tenantId` de la session admin.           */
  /* ---------------------------------------------------------------------- */

  /* ---------------------------------------------------------------------- */
  /* LA BANQUE DE SAVOIRS — console d'édition (Sprint 24).                    */
  /*                                                                        */
  /* Ouverte au TUTEUR et à l'ADMIN. Le corpus est publié d'abord et relu    */
  /* ensuite : ces appels font progresser la relecture pendant que les items */
  /* sont en service. Aucun d'eux ne lit ni n'écrit une donnée d'élève.      */
  /* ---------------------------------------------------------------------- */

  /** La file de relecture, filtrée. Les items jamais relus arrivent d'abord. */
  listerCorpus: (filtres: CorpusFiltres = {}) => {
    const qs = new URLSearchParams();
    for (const [cle, valeur] of Object.entries(filtres)) {
      if (valeur !== undefined && valeur !== '') qs.set(cle, String(valeur));
    }
    return request<CorpusListeResponse>(`/v1/corpus/items${suffixe(qs)}`, { auth: true });
  },

  /**
   * Les savoirs du corpus — pour peupler les filtres ET cadrer l'atelier.
   *
   * Élargi le 14 septembre 2026. Le compte de notions suffisait à un filtre de
   * relecture ; il ne suffit pas à la conception, où l'on choisit l'année, puis
   * le savoir, PUIS la notion que l'exercice travaille.
   *
   * `sequences` porte la scission du programme en 4e et 5e — CST, TS, SN. Vide
   * = commun à toutes les séquences, jamais « aucune ». Sans elle, l'atelier
   * proposerait de composer un exercice de TS pour une classe de CST.
   */
  savoirsDuCorpus: () =>
    request<{
      savoirs: readonly {
        id: string;
        code: string;
        enonce: string;
        matiere: string;
        niveau: string;
        categorie: string | null;
        sequences: readonly string[];
        notions: readonly { id: string; code: string; titre: string; ordre: number }[];
        _count: { notions: number };
      }[];
    }>('/v1/corpus/savoirs', { auth: true }),

  /** Le détail d'un item, avec ses erreurs types pointées et son historique. */
  ouvrirItemCorpus: (id: string) =>
    request<CorpusItemDetail>(`/v1/corpus/items/${encodeURIComponent(id)}`, { auth: true }),

  /**
   * Enregistrer une correction. `portee: 'forme'` édite en place et fait
   * avancer la version ; `portee: 'fond'` RETIRE l'item du service au lieu de
   * le réécrire — parce qu'un item de fond corrigé en place ferait mentir les
   * mesures déjà écrites sur lui.
   */
  corrigerItemCorpus: (id: string, correction: CorpusEditionRequest) =>
    request<{
      action: 'editer_en_place' | 'retirer_et_remplacer';
      motifDecision: string;
      item: CorpusItemDetail | null;
    }>(`/v1/corpus/items/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: correction,
      auth: true,
    }),

  /**
   * ÉCRIRE UN EXERCICE depuis l'atelier — il naît en BROUILLON, jamais publié.
   *
   * Entre l'atelier et l'élève, il y a une relecture : c'est ce qui distingue
   * cette voie de la génération à la demande, qui publie sans revue parce
   * qu'elle sert un élève précis dans l'instant.
   *
   * La notion se désigne par son CODE — stable, lisible, dérivé du savoir. La
   * matière et l'année en découlent : la console ne les saisit pas, donc elle
   * ne peut pas se tromper.
   */
  creerItemCorpus: (input: {
    notionCode: string;
    type: string;
    exigence: string;
    difficulteAuteur: number;
    methodeVerification: string;
    titre: string;
    texte: string;
    consigne: string;
    explication: string;
    options?: readonly { id: string; texte: string; erreurType?: string }[];
    reponsesAcceptees?: readonly string[];
    indices?: readonly string[];
    figure?: Record<string, unknown>;
  }) => request<CorpusItemDetail>('/v1/corpus/items', { method: 'POST', body: input, auth: true }),

  /**
   * SUPPRIMER un exercice — refusé (409) dès qu'une mesure le retient.
   *
   * Des réponses d'élèves peuvent y renvoyer : les effacer laisserait ces
   * traces dans le vide. L'écran proposera alors « Retirer », qui sort l'item
   * du service en gardant la mesure lisible.
   */
  supprimerItemCorpus: (id: string) =>
    request<null>(`/v1/corpus/items/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      auth: true,
    }),

  /* --------------------------------------- servir un exercice à quelqu'un */

  /* Le fondateur, 15 septembre 2026 : « un prof devrait même pouvoir servir
     l'exercice à disposition directe d'un élève ou d'un groupe d'élèves ».
     L'exercice servi passe AVANT le plan, et cesse d'être servi une fois fait. */
  servirExerciceAEleve: (eleveId: string, contentItemId: string, consigne?: string) =>
    request<{ eleveId: string; deja: boolean }>(
      `/v1/tuteur/eleves/${encodeURIComponent(eleveId)}/taches`,
      { method: 'POST', body: { contentItemId, ...(consigne ? { consigne } : {}) }, auth: true },
    ),

  servirExerciceAClasse: (classeId: string, contentItemId: string, consigne?: string) =>
    request<{ nbServis: number; nbDeja: number }>(
      `/v1/tuteur/classes/${encodeURIComponent(classeId)}/taches`,
      { method: 'POST', body: { contentItemId, ...(consigne ? { consigne } : {}) }, auth: true },
    ),

  /** « J'ai lu, rien à changer » — l'item est promu dans la banque. */
  marquerItemRelu: (id: string) =>
    request<CorpusItemDetail>(`/v1/corpus/items/${encodeURIComponent(id)}/relu`, {
      method: 'POST',
      auth: true,
    }),

  /**
   * RATTACHER UN ÉLÈVE PAR SON CODE DE SUIVI.
   *
   * Le fondateur, 14 septembre 2026 : « ajouter un élève à son suivi en
   * utilisant un code de suivi style NIP offert par l'élève ». C'est la seule
   * porte d'entrée d'un élève dans le périmètre d'un professeur.
   *
   * La saisie est envoyée telle que le professeur l'a recopiée — tirets,
   * espaces et casse sont normalisés côté serveur. Refuser une saisie juste
   * serait la pire façon d'échouer.
   */
  rattacherParCodeSuivi: (code: string) =>
    request<{ eleveId: string; prenom: string; affectationId: string; deja: boolean }>(
      '/v1/tuteur/eleves/rattacher',
      { method: 'POST', body: { code }, auth: true },
    ),

  /* ------------------------------------------------------------ la classe */

  /* Une classe ORGANISE, elle ne donne pas accès : on n'y range qu'un élève
     déjà suivi, et l'en retirer ne retire rien de son suivi. */
  listerClasses: () => request<ClassesReponse>('/v1/tuteur/classes', { auth: true }),

  creerClasse: (nom: string) =>
    request<{ classe: ClasseVue }>('/v1/tuteur/classes', {
      method: 'POST',
      body: { nom },
      auth: true,
    }),

  renommerClasse: (classeId: string, nom: string) =>
    request<{ classe: ClasseVue }>(`/v1/tuteur/classes/${encodeURIComponent(classeId)}`, {
      method: 'PATCH',
      body: { nom },
      auth: true,
    }),

  ajouterEleveAClasse: (classeId: string, eleveId: string) =>
    request<{ classe: ClasseVue }>(
      `/v1/tuteur/classes/${encodeURIComponent(classeId)}/eleves/${encodeURIComponent(eleveId)}`,
      { method: 'POST', auth: true },
    ),

  retirerEleveDeClasse: (classeId: string, eleveId: string) =>
    request<{ classe: ClasseVue }>(
      `/v1/tuteur/classes/${encodeURIComponent(classeId)}/eleves/${encodeURIComponent(eleveId)}`,
      { method: 'DELETE', auth: true },
    ),

  /** Les affectations d'une organisation. `actives: false` inclut l'historique. */
  listAffectations: (orgId: string, actives = true) => {
    const qs = new URLSearchParams({ actives: String(actives) });
    return request<ListeAffectationsResponse>(
      `/v1/organisations/${encodeURIComponent(orgId)}/affectations${suffixe(qs)}`,
      { auth: true },
    );
  },

  /**
   * Affecte un élève à un tuteur. Idempotent côté serveur : réaffecter un
   * couple déjà actif renvoie l'affectation existante, sans doublon.
   */
  creerAffectation: (orgId: string, input: CreerAffectationRequest) =>
    request<AffectationResponse>(`/v1/organisations/${encodeURIComponent(orgId)}/affectations`, {
      method: 'POST',
      body: input,
      auth: true,
    }),

  /**
   * Retire une affectation. Ne SUPPRIME jamais la ligne : elle est datée
   * (`fin`). Savoir qui a suivi un mineur, et entre quelles dates, est une
   * exigence de conformité autant que de confiance (§10.8).
   */
  retirerAffectation: (orgId: string, affectationId: string) =>
    request<AffectationResponse>(
      `/v1/organisations/${encodeURIComponent(orgId)}/affectations/${encodeURIComponent(affectationId)}`,
      { method: 'DELETE', auth: true },
    ),
} as const;

export { BASE_URL };
